import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from '../components/FormProvider';
import { useStepsConfig } from '../contexts/StepsConfigContext';
import { getValidationRulesForStep, getAllStepIds } from '../config/eventConfigs';

export function useFormValidation() {
  const { t } = useTranslation();
  const { formData, setValidationError, clearValidationError } = useFormContext();
  const stepsConfig = useStepsConfig();

  const validateField = useCallback((field, value) => {
    // Clear existing error first
    clearValidationError(field);

    // Get validation rules for all steps
    const allStepIds = getAllStepIds(stepsConfig);
    const allRules = allStepIds.reduce((acc, stepId) => {
      const rules = getValidationRulesForStep(stepsConfig, stepId);
      if (rules.required?.includes(field)) {
        acc.required = true;
      }
      if (rules.minLength?.[field]) {
        acc.minLength = rules.minLength[field];
      }
      return acc;
    }, {});

    // Required validation
    if (allRules.required && (!value || value.toString().trim() === '')) {
      setValidationError(field, t('This field is required'));
      return false;
    }

    // Min length validation
    if (allRules.minLength && value && value.toString().length < allRules.minLength) {
      setValidationError(field, t('Minimum {{length}} characters required', { length: allRules.minLength }));
      return false;
    }

    return true;
  }, [clearValidationError, setValidationError, stepsConfig, t]);

  const validateStep = useCallback((stepId) => {
    const rules = getValidationRulesForStep(stepsConfig, stepId);
    if (!rules || Object.keys(rules).length === 0) return true;

    let isValid = true;

    // Validate required fields
    if (rules.required) {
      for (const field of rules.required) {
        const value = formData[field];
        if (!validateField(field, value)) {
          isValid = false;
        }
      }
    }

    // Custom validation
    if (rules.custom && !rules.custom(formData)) {
      isValid = false;
      // Add generic error for custom validation
      setValidationError(stepId, t('Please check your inputs'));
    }

    return isValid;
  }, [formData, validateField, stepsConfig, setValidationError, t]);

  const validateAllSteps = useCallback(() => {
    let isValid = true;
    
    const allStepIds = getAllStepIds(stepsConfig);
    allStepIds.forEach(stepId => {
      if (!validateStep(stepId)) {
        isValid = false;
      }
    });

    return isValid;
  }, [validateStep, stepsConfig]);

  return {
    validateField,
    validateStep,
    validateAllSteps
  };
}